import { camelCase } from "lodash";
import { markRaw } from "vue";
import { UIField } from "../../models/fields";
import { usaStatesListItems } from "../../utils/usaStates";
import { isNPIValid } from "../../utils/validators";
import { valueInItemsAutocompleteRule, requiredRule, valueInItemsRule, isNumericRule } from "../../utils/form";
import { mainServices } from "@/services/mainServices";
import executor from "../../services/api";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";
import MedicationInput from "@/components/intakeReview/MedicationInput.vue";
import DiagnosisCodes from "@/components/intakeReview/DiagnosisCodes.vue";
import ToDoAssignedTo from "@/components/intakeReview/ToDoAssignedTo.vue";

/**
 * Maps a payor name API result to the format expected by the PayorAutocomplete component.
 * @param {Object} payor - The payor object from the API response
 * @param {Object} options - Configuration options
 * @param {boolean} options.useDisplayTextAsValue - If true, uses the full display text as value
 * @returns {Object} Formatted payor item with title, value, bin, and pcn
 */
export const mapPayorNameResult = (payor, options = {}) => {
  const binSuffix = payor.bin ? ` | BIN: ${payor.bin}` : ``;
  const pcnSuffix = payor.pcn ? ` | PCN: ${payor.pcn}` : ``;
  const displayText = `${payor.payorName}${binSuffix}${pcnSuffix}`;
  return {
    title: displayText,
    value: options.useDisplayTextAsValue ? displayText : payor.payorName,
    bin: payor.bin,
    pcn: payor.pcn,
  };
};

/**
 * Creates a function that searches for payor names by BIN and PCN.
 * Results are cached by BIN/PCN pair so repeated calls with the same
 * values (e.g. from validation) reuse the previous response instead of
 * making redundant API requests and re-mutating the shared items array.
 * @param {string} clientCompanyId - The client company ID
 * @param {Array} itemsArray - Array to store the fetched items (mutated in place)
 * @param {Object} options - Configuration options passed to mapPayorNameResult
 * @returns {Function} Search function that takes (bin, pcn) and returns a Promise
 */
export const createPayorNameSearchByBinPcn = (clientCompanyId, itemsArray, options = {}) => {
  let lastBin = null;
  let lastPcn = null;

  return (bin, pcn) => {
    if (!clientCompanyId) {
      return Promise.resolve([]);
    }
    if (bin === lastBin && pcn === lastPcn && itemsArray.length > 0) {
      return Promise.resolve([...itemsArray]);
    }
    lastBin = bin;
    lastPcn = pcn;
    return mainServices.getPayorNamesByBinPcn(clientCompanyId, bin, pcn).then((response) => {
      const items = response.results.map((c) => mapPayorNameResult(c, options));
      itemsArray.length = 0;
      itemsArray.push(...items);
      return items;
    });
  };
};

/**
 * Creates a function that searches for payor names by name.
 * @param {string} clientCompanyId - The client company ID
 * @param {Array} itemsArray - Array to store the fetched items (mutated in place)
 * @param {Object} options - Configuration options
 * @param {boolean} options.useDisplayTextAsValue - If true, uses the full display text as value
 * @param {boolean} options.stripDisplaySuffix - If true, strips " | BIN..." suffix before searching
 * @returns {Function} Search function that takes (value) and returns a Promise
 */
export const createPayorNameSearch = (clientCompanyId, itemsArray, options = {}) => {
  return (value) => {
    if (!clientCompanyId) {
      return Promise.resolve([]);
    }
    let searchValue = value;
    if (options.stripDisplaySuffix && value.includes(" | ")) {
      searchValue = value.split(" | ")[0];
    }
    return mainServices.getPayorNamesByName(clientCompanyId, searchValue).then((response) => {
      const items = response.results.map((c) => mapPayorNameResult(c, options));
      itemsArray.length = 0;
      itemsArray.push(...items);
      return items;
    });
  };
};

/**
 * Creates an onAttributeChange callback that auto-populates BIN/PCN fields when a payor is selected.
 * @param {Array[]} itemsSources - Array of arrays containing payor items to search
 * @param {Object} callbacks - The intakeUI callbacks object
 * @param {Object} insuranceCardFields - The standard insurance card fields
 * @param {Object} insuranceCard - The insurance card object
 * @returns {Function} onAttributeChange callback
 */
export const createPayorNameOnAttributeChange = (itemsSources, callbacks, insuranceCardFields, insuranceCard) => {
  return (value) => {
    let selectedItem = null;
    for (const items of itemsSources) {
      selectedItem = items.find((i) => i.value === value);
      if (selectedItem) break;
    }
    if (selectedItem) {
      if (selectedItem.bin) {
        callbacks.onAttributeChange(selectedItem.bin, insuranceCardFields.rxBin, "INSURANCE_CARD", insuranceCard.id);
      }
      if (selectedItem.pcn) {
        callbacks.onAttributeChange(selectedItem.pcn, insuranceCardFields.rxPcn, "INSURANCE_CARD", insuranceCard.id);
      }
    }
    return value;
  };
};

/**
 * Creates a validation rule that checks if the payor name is valid for the selected BIN/PCN.
 * Yields before reading BIN/PCN so that onAttributeChange (which auto-populates
 * BIN/PCN from a payor selection) has a chance to run first. A monotonic counter
 * discards results from stale invocations when validation fires multiple times
 * in rapid succession.
 * @param {Function} searchByBinPcn - The BIN/PCN search function
 * @param {Object} insuranceCard - The insurance card object
 * @returns {Function} Validation rule function
 */
export const createPayorNameBinPcnValidationRule = (searchByBinPcn, insuranceCard) => {
  let validationId = 0;

  return async (value) => {
    const currentId = ++validationId;

    // Yield to the macrotask queue so onAttributeChange can update BIN/PCN
    // before this rule reads them.
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (currentId !== validationId) return true;

    if (!value || (!insuranceCard.rxBin && !insuranceCard.rxPcn)) {
      return true;
    }
    const binPcnResults = await searchByBinPcn(insuranceCard.rxBin || "", insuranceCard.rxPcn || "");
    if (currentId !== validationId) return true;

    const isValidForBinPcn = binPcnResults ? binPcnResults.some((result) => result.value === value) : true;
    return isValidForBinPcn || "Invalid Payor Name for selected BIN/PCN values";
  };
};

const onChangeIcd10Code = (value) => {
  const icd10SearchPromise = executor.get(`/requirements/search-icd10/?q=${value}`);
  // Axios 0.28 has an issue with AbortController, when upgrading to 1.X this can be updated
  return icd10SearchPromise.then((response) =>
    response.results.map((c) => ({ title: `${c.code} - ${c.name}`, value: c.code }))
  );
};

// eslint-disable-next-line no-unused-vars
export const getStandardOrderFormFields = (clientConfig, callbacks, _orderFormRef) => {
  const getClientMedications = async (searchTerm) => {
    if (!clientConfig?.id) {
      return [];
    }
    const response = await mainServices.getClientMedications(clientConfig?.id, searchTerm);
    return response.map((c) => ({ ...c, title: c.medicationName, value: c.medicationName, routes: c.routes }));
  };

  const weightUnitItems = [
    { title: "", value: "" },
    { title: "lbs", value: "lbs" },
    { title: "kgs", value: "kgs" },
  ];

  const heightUnitItems = [
    { title: "", value: "" },
    { title: "cm", value: "cm" },
    { title: "in", value: "in" },
  ];

  const fields = {
    patientWeightValue: new UIField({
      label: "Weight",
      rules: [isNumericRule],
      key: "patientWeightValue",
      group: "Patient Information",
      metadataKey: "patientWeightValueMetaData",
    }),
    patientWeightUnit: new UIField({
      label: "Weight Unit",
      key: "patientWeightUnit",
      group: "Patient Information",
      metadataKey: "patientWeightUnitMetaData",
      rules: [(v) => valueInItemsRule(v, weightUnitItems)],
      component: "v-select",
      vBind: {
        items: weightUnitItems,
      },
    }),
    patientHeight: new UIField({
      label: "Height",
      key: "patientHeight",
      group: "Patient Information",
      metadataKey: "patientHeightMetaData",
      isExtraData: true,
      rules: [],
    }),
    patientHeightUnit: new UIField({
      label: "Patient Height Unit",
      key: "patientHeightUnit",
      group: "Patient Information",
      metadataKey: "patientHeightUnitMetaData",
      isExtraData: true,
      rules: [(v) => valueInItemsRule(v, heightUnitItems)],
      component: "v-select",
      vBind: {
        items: heightUnitItems,
      },
    }),
    patientAddress: new UIField({
      label: "Patient Address",
      key: "patientAddress",
      group: "Patient Information",
      metadataKey: "patientAddressMetaData",
    }),
    patientZip: new UIField({
      label: "Patient Zip",
      key: "patientZip",
      group: "Patient Information",
      metadataKey: "patientZipMetaData",
    }),
    patientCity: new UIField({
      label: "Patient City",
      key: "patientCity",
      group: "Patient Information",
      metadataKey: "patientCityMetaData",
    }),
    patientState: new UIField({
      label: "Patient State",
      key: "patientState",
      group: "Patient Information",
      metadataKey: "patientStateMetaData",
      component: "v-select",
      vBind: {
        items: usaStatesListItems,
      },
    }),
    patientCounty: new UIField({
      label: "Patient County",
      key: "patientCounty",
      group: "Patient Information",
      metadataKey: "patientCountyMetaData",
      isExtraData: true,
      rules: [],
    }),
    patientHomePhone: new UIField({
      label: "Patient Home Phone",
      key: "patientHomePhone",
      group: "Patient Information",
      metadataKey: "patientHomePhoneMetaData",
      isExtraData: true,
      rules: [],
    }),
    patientWorkPhone: new UIField({
      label: "Patient Work Phone",
      key: "patientWorkPhone",
      group: "Patient Information",
      metadataKey: "patientWorkPhoneMetaData",
      isExtraData: true,
      rules: [],
    }),
    patientEmail: new UIField({
      label: "Patient Email",
      key: "patientEmail",
      group: "Patient Information",
      metadataKey: "patientEmailMetaData",
    }),
    patientGender: new UIField({
      label: "Patient Gender",
      key: "patientGender",
      metadataKey: "patientGenderMetaData",
      group: "Patient Information",
      component: "v-select",
      rules: [],
      vBind: {
        items: [
          { title: "Male", value: "Male" },
          { title: "Female", value: "Female" },
          { title: "Unspecified", value: "Unspecified" },
        ],
      },
    }),
    patientAllergies: new UIField({
      label: "Allergies",
      rules: [],
      key: "patientAllergies",
      group: "Patient Information",
      metadataKey: "patientAllergiesMetaData",
    }),
    patientStatus: new UIField({
      label: "Patient Status",
      key: "patientStatus",
      group: "Patient Information",
      metadataKey: "patientStatusMetaData",
      rules: [],
    }),
    diagnosisIcd10Code: new UIField({
      label: "Diagnosis ICD-10 Code",
      key: "diagnosisIcd10Code",
      metadataKey: "diagnosisIcd10CodeMetaData",
      group: "Medical Details",
      component: markRaw(RemoteAutocomplete),
      rules: [(v, remoteObj) => valueInItemsAutocompleteRule(v, "Invalid ICD 10 Code", remoteObj)],
      vBind: {
        remoteSearch: onChangeIcd10Code,
      },
    }),
    extraDiagnosisCodes: new UIField({
      label: "Additional Diagnosis Codes",
      group: "Medical Details",
      key: "extraDiagnosisCodes",
      metadataKey: "extraDiagnosisCodesMetaData",
      component: markRaw(DiagnosisCodes),
      rules: [(v, remoteObj) => valueInItemsAutocompleteRule(v, "Invalid ICD 10 Code", remoteObj)],
      defaultValue: [],
    }),
    therapyAdministration: new UIField({
      label: "Medication",
      rules: [
        (v) => {
          return v?.length > 0 ? true : "At least one medication is required";
        },
      ],
      group: "Medical Details",
      key: "therapyAdministration",
      defaultValue: [],
      component: markRaw(MedicationInput),
      vBind: { isPrimaryMedication: true, getClientMedications },
      vOn: {
        highlightBoundingBox: callbacks.onHighlightBoundingBox,
      },
      context: { getClientMedications },
      metadataKey: "therapyAdministrationMetaData",
    }),
    preMedication: new UIField({
      label: "Pre-Medication",
      group: "Medical Details",
      key: "preMedication",
      defaultValue: [],
      component: markRaw(MedicationInput),
      vBind: { isPrimaryMedication: false, getClientMedications },
      vOn: {
        highlightBoundingBox: callbacks.onHighlightBoundingBox,
      },
      context: { getClientMedications },
      metadataKey: "preMedicationMetaData",
    }),
    providerFirstName: new UIField({
      label: "Provider First Name",
      rules: [],
      key: "providerFirstName",
      group: "Provider & Practice Information",
      metadataKey: "providerFirstNameMetaData",
    }),
    providerLastName: new UIField({
      label: "Provider Last Name",
      rules: [],
      key: "providerLastName",
      group: "Provider & Practice Information",
      metadataKey: "providerLastNameMetaData",
    }),
    providerNpi: new UIField({
      label: "Provider NPI",
      rules: [(v) => isNPIValid(v) || "Invalid NPI number"],
      key: "providerNpi",
      group: "Provider & Practice Information",
      metadataKey: "providerNpiMetaData",
      onAttributeChange: (npi) => {
        return npi.replace(/\D/g, "");
      },
    }),
    providerFax: new UIField({
      label: "Provider Fax",
      rules: [],
      key: "providerFax",
      group: "Provider & Practice Information",
      metadataKey: "providerFaxMetaData",
    }),
    providerFaxExtension: new UIField({
      label: "Provider Fax Ext",
      rules: [],
      key: "providerFaxExtension",
      group: "Provider & Practice Information",
    }),
    providerPhone: new UIField({
      label: "Provider Phone",
      key: "providerPhone",
      group: "Provider & Practice Information",
      metadataKey: "providerPhoneMetaData",
    }),
    providerPhoneExtension: new UIField({
      label: "Provider Phone Ext",
      key: "providerPhoneExtension",
      group: "Provider & Practice Information",
    }),
    providerPracticeName: new UIField({
      label: "Practice Name",
      key: "providerPracticeName",
      group: "Provider & Practice Information",
      metadataKey: "providerPracticeNameMetaData",
    }),
    providerAddress: new UIField({
      label: "Practice Address",
      key: "providerAddress",
      group: "Provider & Practice Information",
      metadataKey: "providerAddressMetaData",
    }),
    providerCity: new UIField({
      label: "Practice City",
      key: "providerCity",
      group: "Provider & Practice Information",
      metadataKey: "providerCityMetaData",
    }),
    providerState: new UIField({
      label: "Practice State",
      key: "providerState",
      group: "Provider & Practice Information",
      metadataKey: "providerStateMetaData",
      component: "v-select",
      vBind: {
        items: usaStatesListItems,
      },
    }),
    providerZip: new UIField({
      label: "Practice Zip",
      key: "providerZip",
      group: "Provider & Practice Information",
      metadataKey: "providerZipMetaData",
    }),
    billingProvider: new UIField({
      label: "Billing Provider",
      key: "billingProvider",
      group: "Provider & Practice Information",
      metadataKey: "billingProviderMetaData",
      isExtraData: true,
      rules: [],
    }),
    professionalDesignation: new UIField({
      label: "Professional Designation",
      key: "professionalDesignation",
      group: "Provider & Practice Information",
      metadataKey: "professionalDesignationMetaData",
      isExtraData: true,
      rules: [],
    }),
    providerSpecialty: new UIField({
      label: "Provider Specialty",
      key: "providerSpecialty",
      group: "Provider & Practice Information",
      metadataKey: "providerSpecialtyMetaData",
      isExtraData: true,
      rules: [],
    }),
    providerSite: new UIField({
      label: "Site",
      key: "providerSite",
      group: "Provider & Practice Information",
      metadataKey: "providerSiteMetaData",
      isExtraData: true,
      rules: [],
    }),
    dateOfOrder: new UIField({
      label: "Date of Order",
      rules: [],
      key: "dateOfOrder",
      component: "text-date-input",
      group: "Patient Information",
      metadataKey: "dateOfOrderMetaData",
      defaultValue: null,
    }),
    dateOfExpiration: new UIField({
      label: "Date of Expiration",
      key: "dateOfExpiration",
      group: "Medical Details",
      metadataKey: "dateOfExpirationMetaData",
      component: "text-date-input",
      defaultValue: null,
    }),
  };

  return fields;
};

// eslint-disable-next-line no-unused-vars
export const getStandardInsuranceCardFields = (clientConfig, callbacks, insuranceCardRef) => {
  const onChangePayorName = (value) => {
    const clientCompany = clientConfig?.id;
    if (!clientCompany) {
      return Promise.resolve([]);
    }
    const payorNameSearchPromise = executor.get(`/requirements/client/${clientCompany}/payor-names/?q=${value}`);

    // Axios 0.28 has an issue with AbortController, when upgrading to 1.X this can be updated
    return payorNameSearchPromise.then((response) => {
      fetchedPayorNames = response.results.map((c) => ({ title: `${c.payorName}`, value: c.payorName }));
      return fetchedPayorNames;
    });
  };

  let fetchedPayorNames = [];
  const payorNameWarning = (v, warningText = null) => {
    if (fetchedPayorNames.map((p) => p.value).includes(v)) {
      return null;
    }
    return warningText || "Payor name not found in EHR. Can not save to EHR";
  };

  const fields = {
    memberName: new UIField({
      label: "Member",
      rules: [],
      key: "memberName",
      metadataKey: "memberNameMetaData",
    }),
    memberRelationship: new UIField({
      label: "Member Relationship",
      key: "memberRelationship",
      metadataKey: "memberRelationshipMetaData",
      component: "v-select",
      vBind: {
        items: [
          { title: "Self", value: "Self" },
          { title: "Spouse", value: "Spouse" },
          { title: "Child", value: "Child" },
          { title: "Parent", value: "Parent" },
          { title: "Other", value: "Other" },
        ],
      },
    }),
    memberDob: new UIField({
      label: "Member Date of Birth",
      rules: [requiredRule],
      key: "memberDob",
      metadataKey: "memberDobMetaData",
      component: "text-date-input",
      defaultValue: null,
    }),
    memberGender: new UIField({
      label: "Member Gender",
      key: "memberGender",
      metadataKey: "memberGenderMetaData",
      component: "v-select",
      vBind: {
        rules: [requiredRule],
        items: [
          { title: "Male", value: "Male" },
          { title: "Female", value: "Female" },
        ],
      },
    }),
    payorName: new UIField({
      label: "Payor",
      key: "payorName",
      component: markRaw(RemoteAutocomplete),
      metadataKey: "payorNameMetaData",
      rules: [],
      defaultValue: null,
      vBind: {
        remoteSearch: onChangePayorName,
        allowFreeText: true,
      },
      warnings: [],
    }),
    _payorNameWarningFn: payorNameWarning,
    state: new UIField({
      label: "State",
      rules: [],
      key: "state",
      component: "v-select",
      vBind: {
        items: usaStatesListItems,
      },
    }),
    groupNumber: new UIField({
      label: "Group Number",
      key: "groupNumber",
      metadataKey: "groupNumberMetaData",
    }),
    memberId: new UIField({
      label: "Member ID",
      rules: [],
      key: "memberId",
      metadataKey: "memberIdMetaData",
    }),
    insurancePlan: new UIField({
      label: "Plan",
      key: "insurancePlan",
      metadataKey: "insurancePlanMetaData",
    }),
    benefitType: new UIField({
      label: "Benefit Type",
      key: "benefitType",
      metadataKey: "benefitTypeMetaData",
      component: "v-select",
      onAttributeChange: (value) => {
        fields.rxBin.forceRedraw();
        return value;
      },
      vBind: {
        items: [
          { title: "Medical", value: "MEDICAL" },
          { title: "Pharmacy", value: "PHARMACY" },
        ],
      },
    }),
    cardRole: new UIField({
      label: "Card Role",
      key: "cardRole",
      metadataKey: "cardRoleMetaData",
      component: "v-select",
      rules: [],
      vBind: {
        items: [
          { title: "Primary", value: "PRIMARY" },
          { title: "Secondary", value: "SECONDARY" },
        ],
      },
    }),
    rxBin: new UIField({
      label: "RXBIN",
      rules: insuranceCardRef.benefitType === "PHARMACY" ? [requiredRule] : [],
      key: "rxBin",
      metadataKey: "rxBinMetaData",
    }),
    rxGrp: new UIField({
      label: "RXGROUP",
      key: "rxGrp",
      metadataKey: "rxGrpMetaData",
    }),
    rxPcn: new UIField({
      label: "RXPCN",
      key: "rxPcn",
      metadataKey: "rxPcnMetaData",
    }),
    policyOwner: new UIField({
      label: "Policy Owner",
      key: "policyOwner",
      metadataKey: "policyOwnerMetaData",
      isExtraData: true,
      rules: [],
    }),
  };

  return fields;
};

// eslint-disable-next-line no-unused-vars
export const getStandardToDoFields = (clientConfig, _callbacks, _toDoRef) => {
  const onChangeUserAssignedTo = (value) => {
    const clientCompany = clientConfig?.id;
    if (!clientCompany) {
      return Promise.resolve([]);
    }
    const userSearchPromise = executor.get(`/requirements/client/${clientCompany}/users/?q=${value}`);

    // Axios 0.28 has an issue with AbortController, when upgrading to 1.X this can be updated
    return userSearchPromise.then((response) => {
      return response.results.map((c) => ({ title: `${c.fullName}`, value: c.fullName }));
    });
  };

  return {
    toDo: new UIField({
      label: "To Do",
      rules: [requiredRule],
      key: "toDo",
      metadataKey: "toDoMetaData",
    }),
    dueDate: new UIField({
      label: "Due Date",
      key: "dueDate",
      metadataKey: "dueDateMetaData",
      component: "text-date-input",
      defaultValue: null,
      vBind: {
        maxDate: null,
      },
    }),
    priority: new UIField({
      label: "Priority",
      key: "priority",
      metadataKey: "priorityMetaData",
    }),
    department: new UIField({
      label: "Department",
      key: "department",
      metadataKey: "departmentMetaData",
    }),
    type: new UIField({
      label: "Type",
      key: "type",
      metadataKey: "typeMetaData",
    }),
    assignedTo: new UIField({
      label: "Assigned To",
      key: "assignedTo",
      metadataKey: "assignedToMetaData",
      component: markRaw(ToDoAssignedTo),
      rules: [
        (v) => {
          return v?.length > 0 ? true : "At least one user needs to be assigned";
        },
        (v) => {
          return v.every((user) => user && user.trim()) ? true : "No empty user is allowed for assigning";
        },
        (v) => {
          const uniqueValues = new Set(v.map((user) => user?.trim().toLowerCase()));
          return uniqueValues.size === v.length ? true : "Duplicate users are not allowed";
        },
      ],
      vBind: {
        remoteSearch: onChangeUserAssignedTo,
      },
    }),
    notes: new UIField({
      label: "Notes",
      key: "notes",
      metadataKey: "notesMetaData",
    }),
  };
};

/**
 * Apply ToDo classification filtering and validation.
 * Filters the ToDo dropdown to only show classifications currently in use by document pages,
 * and adds validation to ensure selected ToDo matches a page classification.
 * Also prevents duplicate ToDo values across all ToDos.
 *
 * @param {UIFieldList} fields - The ToDo fields list
 * @param {Object} pageManager - The page manager containing pages
 * @param {Object} dropdownFields - Client config dropdown fields
 * @param {Object} currentToDo - The current ToDo being edited (optional)
 * @param {Array} allToDos - All ToDos for duplicate checking (optional)
 */
export const applyToDoClassificationFilter = (
  fields,
  pageManager,
  dropdownFields,
  currentToDo = null,
  allToDos = []
) => {
  const toDoField = fields.getField("toDo");
  if (toDoField) {
    const allToDoItems = dropdownFields?.toDo ?? [];
    // Get unique document types (classifications) from all pages
    const usedClassifications = [
      ...new Set((pageManager?.pages ?? []).map((page) => page.documentType).filter(Boolean)),
    ];

    // Get toDo values already in use
    const usedToDoValues = allToDos.filter((todo) => todo.id !== currentToDo?.id && todo.toDo).map((todo) => todo.toDo);

    // Filter items to only include those matching used classifications AND not already used by other ToDos
    toDoField.vBind.items = allToDoItems.filter((item) => {
      const itemValue = typeof item === "object" ? item.value : item;
      const matchesClassification = usedClassifications.includes(itemValue);
      const notAlreadyUsed = !usedToDoValues.includes(itemValue);
      return matchesClassification && notAlreadyUsed;
    });

    // Validation: if a ToDo is selected but its classification is later removed, show error
    fields.addRule(
      ["toDo"],
      (v) => {
        return pageManager?.pages?.some((page) => page.documentType === v)
          ? true
          : "To Do value must match one of Document Classifications selected";
      },
      0
    );

    // Validation: prevent duplicate ToDo values across all ToDos
    fields.addRule(
      ["toDo"],
      (v) => {
        if (!v) return true;
        const isDuplicate = allToDos.some((todo) => todo.id !== currentToDo?.id && todo.toDo === v);
        return isDuplicate ? "Another To Do already exists with this classification" : true;
      },
      0
    );
  }
};

/** Common logic */
export const insuranceCardUIRules = {
  benefitTypeHiddenFields: (insuranceCard, fields) => {
    if (insuranceCard.benefitType === "MEDICAL") {
      fields.getField("rxBin").hidden = true;
      fields.getField("rxGrp").hidden = true;
      fields.getField("rxPcn").hidden = true;
      fields.getField("payorName").component = markRaw(RemoteAutocomplete);
    }
  },
};

export class BaseIntakeUI {
  constructor(clientConfig, callbacks, stage, pageManager = null) {
    this.clientConfig = clientConfig;
    this.callbacks = callbacks;
    this.stage = stage;
    this.pageManager = pageManager;
  }

  async initialize() {}

  getOrderFormFields() {
    return [];
  }

  getInsuranceCardFields() {
    return [];
  }

  // eslint-disable-next-line no-unused-vars
  getToDosFields(_toDo, _allToDos = []) {
    return [];
  }

  /**
   * Called when a patient is selected in the intake review screen.
   * Client-specific implementations can override this to add custom behavior.
   * @param {Object} patient - The selected patient object
   * @param {Object} context - Context object containing documentInfo, intakeService, onAttributeEdit, fetchIntakeDocument
   * @returns {Promise<void>}
   */
  async onPatientSelected(_patient, _context) {
    // Default implementation does nothing
    // Client-specific classes can override this
    return null;
  }

  /**
   * Determines if an order form should be shown/created based on the current document classifications.
   * Client-specific implementations can override this to add custom behavior.
   * @returns {boolean} - True if order form should be shown
   */
  shouldShowOrderForm() {
    // Default: always allow order form
    return true;
  }

  /**
   * Determines if insurance cards should be shown/created based on the current document classifications.
   * Client-specific implementations can override this to add custom behavior.
   * @returns {boolean} - True if insurance cards should be shown
   */
  shouldShowInsuranceCard() {
    // Default: always allow insurance cards
    return true;
  }

  /**
   * Determines if a ToDo should be shown/created based on the current document classifications.
   * Client-specific implementations can override this to add custom behavior.
   * @returns {boolean} - True if ToDo should be shown
   */
  shouldShowToDo() {
    // Default: check if getToDosFields returns fields
    try {
      const sampleToDoFields = this.getToDosFields({});
      return sampleToDoFields?.fields?.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Determines if a ToDo is required based on the current document classifications.
   * Client-specific implementations can override this to add custom behavior.
   * @returns {boolean} - True if ToDo is required for submission
   */
  isToDoRequired() {
    // Default: ToDo is not required
    return false;
  }

  /** Intake Review Page */
  getInboxSubmitButtonText({ orderForm }) {
    const inboxOnly = this.clientConfig?.intake?.inboxOnly;
    if (!inboxOnly && orderForm !== null) {
      return "Continue to Qualify";
    }
    return "Send to EHR";
  }

  /**
   * Returns an array of intake-level warnings to display.
   * Override in client-specific implementations.
   * @param {Object} context - Contains patient, pages, etc.
   * @returns {Array<{type: string, message: string}>} Warning objects
   */
  // eslint-disable-next-line no-unused-vars
  getIntakeWarnings({}) {
    // Default: no warnings (disabled for all clients by default)
    return [];
  }

  /**
   * Client-specific validation before intake submission.
   * Default implementation returns no errors.
   * Client-specific classes can override this.
   * @param {Object} _patient - The patient object
   * @param {Object} _orderForm - The order form object
   * @returns {string|null} - Error message if validation fails, null otherwise
   */
  validateIntakeSubmit(_patient, _orderForm) {
    return null;
  }
}

export const getClientIntakeUI = async (clientConfig, callbacks, stage, pageManager = null) => {
  let clientSpecific;
  try {
    const clientSpecificKey = camelCase(clientConfig.key);
    clientSpecific = await import(`@/clientSpecific/${clientSpecificKey}/intakeUI`);
    if (!clientSpecific.IntakeUI) {
      throw new Error("Client specific intake UI not found");
    }
  } catch (e) {
    clientSpecific = await import(`@/clientSpecific/lamarhealth/intakeUI`);
  }
  const intakeUI = new clientSpecific.IntakeUI(clientConfig, callbacks, stage, pageManager);
  await intakeUI.initialize();
  return intakeUI;
};
